import { Button, Input, Spin, Tooltip } from 'antd';
import clsx from 'clsx';
import MarkdownRenderer from '../../components/Common/MarkdownRenderer';
import TotpSetupDrawer from '../../components/Common/TotpSetupDrawer';
import { Icon } from '../../components/Core/Icon';
import { useBranding } from '../../hooks/useBranding';
import useLogin from '../../hooks/useLogin';
import type { OAuthProvider } from '../../interfaces/UserApiService';
import {
  getBrandingDataDefaultImage,
  isPasskeyFeatureEnabled,
  resolveLoginPageEmailLabel,
  resolveLoginPageEmailPlaceholder,
} from '../../utils/utils';

/**
 * Sign-in route: email OTP, optional social/direct OAuth, second factor (TOTP and/or passkey), and passwordless passkey.
 */
export const Login = () => {
  const {
    isLoading,
    setForm,
    onSubmit,
    form,
    t,
    isOtpRequested,
    otpTimer,
    handleRequestOtp,
    selectedFile,
    handleRemoveFile,
    isLoggedIn,
    isLoadingOverlayVisible,
    isEmailValid,
    authenticate,
    isPageLoaded,
    otpInputRef,
    isSubmitBlocked,
    submitBlockTimer,
    isRedirectingToOAuth,
    oauthProviders,
    handleDirectOAuthLogin,
    totpRequiredData,
    totpCode,
    setTotpCode,
    handleTotpCodeChange,
    totpInputRef,
    totpRemainingSeconds,
    clearTotpStep,
    handleOpenTotpSetup,
    useBackupCodeMode,
    toggleTotpInputMode,
    chooseTwoFactorTotp,
    chooseTwoFactorPasskey,
    handlePasswordlessLogin,
    showPasswordlessLogin,
    a11yLiveMessage,
    totpSetupRequiredData,
    totpSetupRemainingSeconds,
    handleForcedTotpSetupSuccess,
    handleForcedTotpSetupExpired,
    resetToEmailOnly,
  } = useLogin();
  const { brandingData, isLoadingBrandingData } = useBranding();

  const colClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
  } as const;

  return (
    <>
      <div aria-live="assertive" className="sr-only" role="alert">
        {a11yLiveMessage}
      </div>
      {isLoadingOverlayVisible && (
        <div
          className="absolute inset-0 -top-20 z-50 flex items-center justify-center bg-white transition-all duration-200"
          role="status"
          aria-live="polite"
        >
          <div className="flex flex-row items-center justify-center gap-2">
            <span>{t('a11y.verifyingFile')}</span>
            <Spin aria-hidden />
          </div>
        </div>
      )}

      <div className="flex w-full max-w-md flex-col items-center justify-center">
        <img
          className={clsx(
            'max-w-[150px] transition-all duration-300 ease-in-out',
            (isLoadingBrandingData || !isPageLoaded) && 'opacity-25 blur-lg',
          )}
          loading="eager"
          src={
            brandingData?.loginPageLogo
              ? `${brandingData.loginPageLogo}?v=${Date.now()}`
              : getBrandingDataDefaultImage('login-page')
          }
          alt={brandingData?.companyName || 'Filegrant Enterprise'}
        />
        <h1 className="pt-4 pb-1 text-center leading-6">
          {brandingData?.loginPageTitle || t('auth.loginPageTitle')}
        </h1>
        <h2 className="text-center leading-6">
          {brandingData?.loginPageSubtitle || t('auth.loginPageSubtitle')}
        </h2>
        <form
          className="grid w-full grid-cols-1 overflow-hidden pt-4 transition-all duration-200 [&_.ant-form-item]:mb-2 [&_.ant-form-item-explain-error]:hidden [&_.ant-form-item-label]:hidden"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          {selectedFile && (
            <div className="border-blue-dark mb-4 rounded-md border px-4 py-3">
              <label className="text-blue-dark flex items-center justify-between">
                <span>
                  {isLoggedIn ? selectedFile?.fileProtected?.originaleFileName : 'File Shared'}
                </span>
                {!isLoading && (
                  <button
                    type="button"
                    className=""
                    aria-label={t('a11y.removeSharedFile')}
                    onClick={handleRemoveFile}
                  >
                    <Icon name="XIcon" className="h-5 w-5" />
                  </button>
                )}
              </label>
            </div>
          )}
          <div className="login-email-field border-gray focus-within:border-azure rounded-md border pb-2 transition-colors">
            <label className="text-blue-dark px-2.5 text-sm" htmlFor="login-email">
              {resolveLoginPageEmailLabel(
                brandingData?.loginPageEmailLabel,
                t('auth.loginPageEmailLabel'),
              )}
            </label>
            <Input
              id="login-email"
              type="text"
              autoComplete="username"
              variant="borderless"
              size="small"
              autoFocus
              classNames={{
                root: 'outline-none! shadow-none! [&:focus-visible]:outline-none! [&:has(input:focus-visible)]:outline-none! [&:has(input:focus)]:outline-none!',
                input: 'outline-none! focus-visible:outline-none!',
              }}
              placeholder={resolveLoginPageEmailPlaceholder(
                brandingData?.loginPageEmailPlaceholder,
                t('auth.loginPageEmailPlaceholder'),
              )}
              suffix={
                <Tooltip title={t('common.clear')}>
                  <Button
                    aria-label={t('common.clear')}
                    className={clsx(form.email ? 'opacity-100' : 'pointer-events-none opacity-0')}
                    type="link"
                    size="small"
                    onClick={() => {
                      setForm((prevForm) => ({ ...prevForm, email: '' }));
                      if (totpRequiredData || totpSetupRequiredData) clearTotpStep();
                    }}
                  >
                    <Icon name="XIcon" className="text-gray-dark hover:text-blue-dark h-5 w-5" />
                  </Button>
                </Tooltip>
              }
              disabled={isLoggedIn || isSubmitBlocked}
              required
              value={form.email}
              onChange={(e) => {
                const newEmail = e.target.value;
                setForm((prevForm) => ({ ...prevForm, email: newEmail }));
                if (totpRequiredData || totpSetupRequiredData) clearTotpStep();
              }}
            />
          </div>
          {!isLoggedIn && isOtpRequested && !oauthProviders && (
            <div className="flex flex-col pt-2 transition-all">
              <label className="flex flex-row items-center justify-start" htmlFor="login-otp">
                {brandingData?.loginPageOtpLabel || t('auth.loginPageOtpLabel')}
              </label>
              <div className="mb-0 w-full pt-4 pb-0">
                <Input.OTP
                  id="login-otp"
                  aria-label={t('a11y.otpCode')}
                  size="large"
                  ref={otpInputRef}
                  className="w-full p-0"
                  inputMode="numeric"
                  disabled={
                    !!totpRequiredData ||
                    !!totpSetupRequiredData ||
                    isLoading ||
                    isSubmitBlocked ||
                    isRedirectingToOAuth
                  }
                  value={form.code}
                  separator={() => <span>-</span>}
                  onChange={(e) => {
                    setForm((prevForm) => ({ ...prevForm, code: e }));
                    authenticate(e).then(() => {});
                  }}
                />
              </div>
              <div className="mt-2 flex flex-row justify-end">
                <Button
                  type="link"
                  size="small"
                  onClick={handleRequestOtp}
                  className="text-blue-dark! hover:text-azure! text-sm"
                  disabled={
                    !!totpRequiredData ||
                    !!totpSetupRequiredData ||
                    (otpTimer ? otpTimer > 0 : false) ||
                    isSubmitBlocked ||
                    isRedirectingToOAuth
                  }
                >
                  {brandingData?.loginPageResendOtp || t('auth.loginPageResendOtp')}
                  {otpTimer &&
                    `
                  (${otpTimer})
                    `}
                </Button>
              </div>
            </div>
          )}

          {totpRequiredData && (
            <div className="flex flex-col gap-2 pt-4">
              {totpRequiredData.twoFactorUiMode === 'pick' && (
                <>
                  <span className="font-medium">{t('auth.twoFactorChooseMethod')}</span>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    {isPasskeyFeatureEnabled() && totpRequiredData.passkeyAvailable && (
                      <Button
                        type="primary"
                        size="large"
                        className="flex-1"
                        disabled={isLoading || isSubmitBlocked || isRedirectingToOAuth}
                        onClick={chooseTwoFactorPasskey}
                      >
                        {t('auth.usePasskey')}
                      </Button>
                    )}
                    {totpRequiredData.totpAvailable && (
                      <Button
                        size="large"
                        className="flex-1"
                        disabled={isLoading || isSubmitBlocked || isRedirectingToOAuth}
                        onClick={chooseTwoFactorTotp}
                      >
                        {t('auth.useTotp')}
                      </Button>
                    )}
                  </div>
                </>
              )}
              {totpRequiredData.twoFactorUiMode === 'passkey' && isLoading && (
                <div className="text-gray-dark flex items-center gap-2 py-2 text-sm">
                  <Spin />
                  <span>{t('auth.passkeyWaiting')}</span>
                </div>
              )}
              {totpRequiredData.twoFactorUiMode === 'totp' && (
                <>
                  <label htmlFor="login-totp-backup">
                    {useBackupCodeMode ? t('auth.enterBackupCode') : t('auth.enterTotpCode')}
                  </label>

                  <div className="w-full pt-2">
                    {useBackupCodeMode ? (
                      <Input
                        id="login-totp-backup"
                        aria-label={t('a11y.backupCode')}
                        type="text"
                        size="large"
                        className="w-full"
                        placeholder={t('auth.backupCodePlaceholder')}
                        disabled={isLoading || isSubmitBlocked || isRedirectingToOAuth}
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value)}
                        autoComplete="one-time-code"
                      />
                    ) : (
                      <Input.OTP
                        id="login-totp"
                        aria-label={t('a11y.totpCode')}
                        ref={totpInputRef}
                        length={6}
                        size="large"
                        className="w-full p-0"
                        inputMode="numeric"
                        disabled={isLoading || isSubmitBlocked || isRedirectingToOAuth}
                        value={totpCode}
                        separator={() => <span>-</span>}
                        onChange={handleTotpCodeChange}
                      />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-row items-center justify-between">
                      <Button
                        type="link"
                        size="small"
                        className="text-blue-dark! hover:text-azure! p-0! text-sm!"
                        onClick={toggleTotpInputMode}
                      >
                        {useBackupCodeMode ? t('auth.useOtpCode') : t('auth.useBackupCode')}
                      </Button>
                      <Button
                        type="link"
                        size="small"
                        className="text-blue-dark! hover:text-azure! p-0! text-sm!"
                        onClick={handleOpenTotpSetup}
                      >
                        {t('auth.configure2FA')}
                      </Button>
                    </div>
                  </div>
                </>
              )}
              {totpRemainingSeconds !== null && (
                <p className="text-gray-dark text-xs">
                  {t('auth.totpTimeRemaining', {
                    time: `${Math.floor(totpRemainingSeconds / 60)}:${(totpRemainingSeconds % 60).toString().padStart(2, '0')}`,
                  })}
                </p>
              )}
            </div>
          )}

          {!totpSetupRequiredData && (
            <MarkdownRenderer
              content={brandingData?.loginTermsAndConditions || t('auth.loginTermsAndConditions')}
              className="pt-4 text-center text-sm"
            />
          )}

          {!totpSetupRequiredData && (
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              className="mt-4 w-full"
              aria-busy={isLoading || isRedirectingToOAuth}
              disabled={
                totpRequiredData
                  ? totpRequiredData.twoFactorUiMode === 'pick' ||
                    totpRequiredData.twoFactorUiMode === 'passkey'
                    ? true
                    : useBackupCodeMode
                      ? !totpCode.trim() || isSubmitBlocked
                      : totpCode.length !== 6 || isSubmitBlocked
                  : (isOtpRequested && (!form.code || form.code?.length !== 6)) ||
                    !isEmailValid ||
                    isSubmitBlocked
              }
              loading={isLoading || isRedirectingToOAuth}
            >
              {isLoading || isRedirectingToOAuth ? (
                <span className="sr-only">{t('a11y.loading')}</span>
              ) : null}
              {isSubmitBlocked
                ? t('auth.loginPageTooManyAttempts') + ` ${submitBlockTimer}s`
                : brandingData?.loginButtonLabel || t('auth.loginButtonLabel')}
            </Button>
          )}

          {!isLoggedIn &&
            !totpRequiredData &&
            !totpSetupRequiredData &&
            isPasskeyFeatureEnabled() &&
            showPasswordlessLogin &&
            !oauthProviders && (
              <div className="mt-4 flex flex-col gap-3">
                <div className="relative">
                  <div aria-hidden="true" className="absolute inset-0 flex items-center">
                    <div className="border-gray-medium w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-sm/6 font-medium">
                    <span className="bg-white px-6">{t('auth.orDivider')}</span>
                  </div>
                </div>
                <Button
                  size="large"
                  className="w-full"
                  disabled={isLoading || isSubmitBlocked || isRedirectingToOAuth}
                  loading={isLoading || isRedirectingToOAuth}
                  onClick={() => {
                    void handlePasswordlessLogin();
                  }}
                >
                  {t('auth.signInWithPasskey')}
                </Button>
              </div>
            )}

          <div className="mt-6">
            {/* Direct OAuth Providers (Internal SSO) */}
            {oauthProviders && oauthProviders.length > 0 && (
              <>
                <div className="relative">
                  <div aria-hidden="true" className="absolute inset-0 flex items-center">
                    <div className="border-gray-medium w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-sm/6 font-medium">
                    <span className="bg-white px-6">{t('auth.orContinueWith')}</span>
                  </div>
                </div>
                <div
                  className={clsx(
                    'grid gap-4',
                    colClass[oauthProviders.length as keyof typeof colClass],
                  )}
                >
                  {oauthProviders.map((provider: OAuthProvider) => (
                    <Button
                      key={provider.provider}
                      size="large"
                      className="mt-4 w-full"
                      disabled={!!form.code || isSubmitBlocked}
                      loading={isLoading || isRedirectingToOAuth}
                      onClick={() => handleDirectOAuthLogin(provider)}
                    >
                      <Icon
                        name={
                          provider.provider === 'microsoft' ? 'MicrosoftLogoIcon' : 'GoogleLogoIcon'
                        }
                        className="h-5 w-5"
                      />
                      <span className="text-sm font-semibold">{provider.providerName}</span>
                    </Button>
                  ))}
                </div>
              </>
            )}
          </div>
        </form>
      </div>

      <TotpSetupDrawer
        open={!!totpSetupRequiredData}
        userEmail={totpSetupRequiredData?.userEmail}
        partialAuthToken={totpSetupRequiredData?.partialAuthToken}
        remainingSeconds={totpSetupRemainingSeconds}
        onClose={resetToEmailOnly}
        onSuccess={handleForcedTotpSetupSuccess}
        onTemporaryTotpExpired={handleForcedTotpSetupExpired}
      />
    </>
  );
};

export default Login;
